import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaBalanceScale, FaFileAlt, FaUsers, FaShieldAlt } from 'react-icons/fa';

const WealthEstatePlanningPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Wealth & Estate Planning' }
  ];

  const planningServices = [
    {
      icon: <FaBalanceScale className="w-8 h-8 text-primary" />,
      title: "Estate Strategy",
      description: "Comprehensive estate planning strategies to preserve and transfer wealth efficiently across generations."
    },
    {
      icon: <FaFileAlt className="w-8 h-8 text-primary" />,
      title: "Legal Documentation",
      description: "Work with legal advisors to ensure proper documentation and compliance with estate planning laws."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-primary" />,
      title: "Family Legacy",
      description: "Create lasting legacies that reflect your values and provide for future generations."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-primary" />,
      title: "Tax Optimization",
      description: "Minimize tax implications while maximizing wealth preservation and transfer opportunities."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Wealth & Estate Planning" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/people-discussing-business-plan.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Preserving Prosperity for Life & Legacy
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                The professionals in our wealth and estate planning departments will work alongside your 
                Blockfirst Capital financial professional, as well as your tax and legal advisors, to help you 
                develop a deep understanding of your entire financial picture: where you are, where you're 
                going and planning for where you want to be.
              </p>
              <p className="text-lg text-gray-700">
                Our comprehensive approach ensures that your wealth preservation and estate planning strategies 
                are perfectly aligned with your values, goals, and the legacy you want to leave for future generations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/co-workers-planning-strategy.jpg" 
                alt="Wealth and Estate Planning Consultation" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {planningServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
              >
                <div className="bg-gray-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-custom mb-16"
          >
            <h3 className="text-3xl font-heading font-bold text-primary mb-8 text-center">
              Comprehensive Financial Picture Analysis
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-gray-900">📍</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Where You Are</h4>
                <p className="text-gray-600">
                  Comprehensive assessment of your current financial position, assets, liabilities, and cash flow.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-gray-900">🎯</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Where You're Going</h4>
                <p className="text-gray-600">
                  Analysis of your financial trajectory based on current strategies and market projections.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-gray-900">✨</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Where You Want to Be</h4>
                <p className="text-gray-600">
                  Strategic planning to align your wealth management with your long-term goals and legacy vision.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-custom mb-16"
          >
            <h3 className="text-3xl font-heading font-bold text-primary mb-6 text-center">
              Our Collaborative Approach
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h4 className="text-xl font-semibold text-primary">Professional Team Integration</h4>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span><strong>Blockfirst Capital Financial Professionals:</strong> Investment and wealth management expertise</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span><strong>Tax Advisors:</strong> Strategic tax planning and optimization</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span><strong>Legal Advisors:</strong> Estate law compliance and documentation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span><strong>Estate Planning Specialists:</strong> Legacy and succession planning</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-6">
                <h4 className="text-xl font-semibold text-primary">Key Planning Areas</h4>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Wealth preservation and growth strategies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Tax-efficient wealth transfer techniques</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Charitable giving and philanthropic planning</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Business succession and exit strategies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Trust and estate administration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Insurance and risk management</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Build Your Lasting Legacy</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Start planning for your legacy today. Our wealth and estate planning experts will work with you 
                and your advisory team to create a comprehensive strategy that preserves your prosperity for 
                generations to come.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Begin Your Legacy Planning
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default WealthEstatePlanningPage; 